import { useState, useEffect, useCallback, useRef } from 'react';
import { useFocusEffect } from 'expo-router';
import type { NotificationSummary, NotificationNewEvent } from '@tabbio/types';
import { SOCKET_EVENTS } from '@tabbio/types';
import {
  fetchNotifications,
} from '../lib/notifications-api';
import { runNotificationRequestWithRetry } from '../lib/notification-retry';
import { useSocket } from './useSocket';

/**
 * Hook for fetching and real-time updating of notification list with cursor pagination.
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState<NotificationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const { socket } = useSocket();
  const activeRequest = useRef<AbortController | null>(null);
  const requestSequence = useRef(0);

  // Initial fetch (reset list)
  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    activeRequest.current?.abort();
    try {
      setIsLoading(true);
      setError(null);
      const result = await runNotificationRequestWithRetry({
        fetcher: (signal) => fetchNotifications(undefined, undefined, undefined, signal),
        onAttempt: (controller) => {
          activeRequest.current = controller;
        },
        shouldContinue: () => sequence === requestSequence.current,
      });
      if (sequence !== requestSequence.current) return;
      setNotifications(result.notifications);
      setNextCursor(result.nextCursor);
    } catch (e: any) {
      if (sequence !== requestSequence.current) return;
      setError(e.message ?? 'Failed to load notifications');
    } finally {
      if (sequence === requestSequence.current) setIsLoading(false);
    }
  }, []);

  // Load more (append to list)
  const loadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore) return;
    try {
      setIsLoadingMore(true);
      const result = await fetchNotifications(nextCursor);
      setNotifications((prev) => [...prev, ...result.notifications]);
      setNextCursor(result.nextCursor);
    } catch {
      // silently fail: user can retry
    } finally {
      setIsLoadingMore(false);
    }
  }, [nextCursor, isLoadingMore]);

  useFocusEffect(
    useCallback(() => {
      void load();
      return () => {
        requestSequence.current += 1;
        activeRequest.current?.abort();
      };
    }, [load]),
  );

  useEffect(() => () => activeRequest.current?.abort(), []);

  // Real-time: prepend new notifications
  useEffect(() => {
    if (!socket) return;

    const handleNew = (event: NotificationNewEvent) => {
      setNotifications((prev) => {
        const withoutExisting = prev.filter((n) => n.id !== event.notification.id);
        return [event.notification, ...withoutExisting];
      });
    };

    socket.on(SOCKET_EVENTS.NOTIFICATION_NEW, handleNew);
    return () => {
      socket.off(SOCKET_EVENTS.NOTIFICATION_NEW, handleNew);
    };
  }, [socket]);

  // Helper to update a single notification locally (e.g. after mark as read)
  const updateNotification = useCallback(
    (id: string, updates: Partial<NotificationSummary>) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, ...updates } : n)),
      );
    },
    [],
  );

  // Helper to remove a notification locally
  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Mark all as read locally
  const markAllReadLocal = useCallback(() => {
    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        read: true,
        readAt: n.readAt ?? new Date().toISOString(),
      })),
    );
  }, []);

  return {
    notifications,
    isLoading,
    isLoadingMore,
    error,
    hasMore: !!nextCursor,
    refetch: load,
    loadMore,
    updateNotification,
    removeNotification,
    markAllReadLocal,
  };
}
